// LetsBeginModal.jsx
//
// 6 steps: name, email+mobile, password, country+terms, basics & lifestyle,
// profile photo. The last step now submits registration.
//
// Submission switched from a JSON axios.post to multipart FormData, since
// this now optionally carries an image file — the backend
// (authRoutes.js + authController.register()) needs matching multer
// middleware to parse it.
//
// Cancel (X) handling: every step receives onCancel. It closes the modal AND
// resets the step + form, so reopening the popup starts clean from step 1.

import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import { toast } from "../../components/common/Toast";
import SignupStep1 from "./Step1"; // Full name
import SignupStep2 from "./Step2"; // Email & mobile
import SignupStep3 from "./Step3"; // Password
import SignupStep4 from "./Step4"; // Country & terms
import SignupStep5 from "./Step5"; // Basics & lifestyle
import SignupStep6 from "./Step6"; // Profile photo (submits)

const INITIAL_FORM_DATA = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  country: "",
  acceptedTerms: false,
  // Basics & Lifestyle (Step 5)
  dob: "",
  maritalStatus: "",
  height: "",
  grewUpIn: "",
  diet: "",
  bloodGroup: "",
  disability: "",
  // Profile photo (Step 6)
  profileImage: null,
};

const LetsBeginModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const nextStep = () => setCurrentStep(currentStep + 1);

  // Back button: step 1 closes the popup, other steps go back one step
  const prevStep = () => {
    if (currentStep === 1) {
      handleCancel();
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  // Close the popup and reset everything for the next time it opens
  const handleCancel = () => {
    setCurrentStep(1);
    setFormData(INITIAL_FORM_DATA);
    setSubmitting(false);
    setSubmitted(false);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("firstName", formData.firstName);
      payload.append("lastName", formData.lastName);
      payload.append("email", formData.email);
      payload.append("phone", formData.phone);
      payload.append("password", formData.password);
      payload.append("country", formData.country);
      payload.append("acceptedTerms", formData.acceptedTerms);
      if (formData.dob) payload.append("dob", formData.dob);
      if (formData.maritalStatus) payload.append("maritalStatus", formData.maritalStatus);
      if (formData.height) payload.append("height", formData.height);
      if (formData.grewUpIn) payload.append("grewUpIn", formData.grewUpIn);
      if (formData.diet) payload.append("diet", formData.diet);
      if (formData.bloodGroup) payload.append("bloodGroup", formData.bloodGroup);
      if (formData.disability) payload.append("disability", formData.disability);
      if (formData.profileImage) payload.append("profileImage", formData.profileImage);

      const response = await axios.post(`${config.baseURL}/api/auth/register`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setSubmitted(true);
      }
    } catch (err) {
      const message = err.response?.data?.message || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    handleCancel();
    navigate("/login");
  };

  const stepProps = {
    formData,
    setFormData,
    nextStep,
    prevStep,
    onCancel: handleCancel,
  };

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      className="letsbeginmodal"
    >
      <Modal.Body>
        {submitted ? (
          <div className="form-basic">
            <div className="icon-blk text-center">
              <img src="images/profile-icon.svg" alt="Profile" />
            </div>
            <h5 className="modal-title text-center">Check your email</h5>
            <p className="text-center">
              We've sent a verification link to <strong>{formData.email}</strong>.
              Click it to activate your account, then come back and log in.
            </p>
            <div className="modal-footer border-0 justify-content-center p-0 footer-modal mt-4">
              <button type="button" className="py-2 btn-filled" onClick={handleDone}>
                Go to login
              </button>
            </div>
          </div>
        ) : (
          <>
            {currentStep === 1 && <SignupStep1 {...stepProps} />}
            {currentStep === 2 && <SignupStep2 {...stepProps} />}
            {currentStep === 3 && <SignupStep3 {...stepProps} />}
            {currentStep === 4 && <SignupStep4 {...stepProps} />}
            {currentStep === 5 && <SignupStep5 {...stepProps} />}
            {currentStep === 6 && (
              <SignupStep6
                {...stepProps}
                onSubmit={handleSubmit}
                submitting={submitting}
              />
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LetsBeginModal;